import i18n from 'locales/i18n';
import { Trans } from 'react-i18next';
import {
	BoldVerticalBarChartData,
	BoldVerticalBarChartProps,
} from '@components/report/chart/bold-vertical-bar-chart/BoldVerticalBarChart';
import { formatBoldVerticalChartData } from '@components/report/chart/utils';
import { VerticalBarAverageChartProps } from '@components/report/chart/vertical-bar-average-chart/VerticalBarAverageChart';

import { YTReportRes } from '@apis/orval/model';

import round, { roundFloat } from '@utils/round';

import { EngagementYtProps } from './EngagementYt';

export class AverageViewsAdapter {
	value: YTReportRes;

	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	get avgViews() {
		return this.value?.engagement?.avg_view ?? undefined;
	}

	adapt(): VerticalBarAverageChartProps {
		return {
			desc: (
				// <>
				// 	평균 조회 수는 <span>{this.avgViews ? round(this.avgViews, 'down') : '-'} 회</span>입니다.
				// </>
				<Trans
					// t={i18n?.t}
					i18nKey={'infReportYoutube:engagement.avg-views.description'}
					components={[
						<span key={'views'}>
							{i18n?.t('unit.times', {
								number: this.avgViews ? round(this.avgViews, 'down') : '-',
							})}
						</span>,
					]}
				/>
			),
			barChartValues: [70, 60, 40, 50, 30, 55, 65],
			value: this.avgViews ?? undefined,
			isPositive: true,
		};
	}
}

export class AverageLikesAdapter {
	value: YTReportRes;

	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	get isAvgLikePrivate() {
		return this.value?.engagement?.is_avg_like_private ?? false;
	}

	get avgLikes() {
		if (this.isAvgLikePrivate) return undefined;
		return this.value?.engagement?.avg_like ?? undefined;
	}

	get primaryText() {
		if (this.isAvgLikePrivate)
			return i18n?.t('infReportYoutube:private-content.private'); //'비공개'
		else if (this.avgLikes === undefined) return i18n?.t('unit.counts', { number: '-' });
		else return i18n?.t('unit.counts', { number: round(this.avgLikes, 'down') });
	}

	adapt(): VerticalBarAverageChartProps {
		return {
			desc: (
				// <>
				// 	평균 좋아요 수는 <span>{this.avgLikes ? round(this.avgLikes, 'down') : '-'} 개</span>입니다.
				// </>
				<Trans
					// t={i18n?.t}
					i18nKey={'infReportYoutube:engagement.avg-likes.description'}
					components={[<span key={'likes'}>{this.primaryText}</span>]}
				/>
			),
			barChartValues: [60, 80, 70, 50, 40, 60, 75],
			value: this.avgLikes,
			isPositive: true,
		};
	}
}

export class AverageCommentsAdapter {
	value: YTReportRes;

	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	get isAvgCommentPrivate() {
		return this.value?.engagement?.is_avg_comment_private ?? false;
	}

	get avgComments() {
		if (this.isAvgCommentPrivate) return undefined;
		return this.value?.engagement?.avg_comment ?? undefined;
	}

	get primaryText() {
		if (this.isAvgCommentPrivate)
			return i18n?.t('infReportYoutube:private-content.inactivate'); //'비활성화'
		else if (this.avgComments === undefined)
			return i18n?.t('unit.counts', { number: '-' });
		else return i18n?.t('unit.counts', { number: round(this.avgComments, 'down') });
	}

	adapt(): VerticalBarAverageChartProps {
		return {
			desc: (
				// <>
				// 	평균 댓글 수는 <span>{this.avgComments ? round(this.avgComments, 'down') : '-'} 개</span>입니다.
				// </>
				<Trans
					// t={i18n?.t}
					i18nKey={'infReportYoutube:engagement.avg-comments.description'}
					components={[<span key={'comments'}>{this.primaryText}</span>]}
				/>
			),
			barChartValues: [30, 50, 35, 75, 80, 55, 40],
			value: this.avgComments,
			isPositive: true,
		};
	}
}

export class EngagementRateAdapter {
	value: YTReportRes;

	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	/**
	 * @description 참여율(ER)
	 */
	get realEngagement() {
		return this.value?.engagement?.real_engagement ?? undefined;
	}
	/**
	 * @description 유사 그룹 평균 참여율(ER)
	 */
	get avgRealEngagement() {
		return this.value?.engagement?.avg_similar_group_real_engagement ?? undefined;
	}

	/**
	 * @description 참여율(ER) 비교 결과 텍스트
	 */
	textMapper = {
		high: (
			<Trans
				// t={i18n?.t}
				i18nKey={'infReportYoutube:engagement.engagement-rate.high-comparison'}
				components={[
					<span key="high">
						{i18n?.t('infReportYoutube:engagement.engagement-rate.high')}
					</span>,
				]}
			/>
		),
		normal: (
			<Trans
				// t={i18n?.t}
				i18nKey={'infReportYoutube:engagement.engagement-rate.normal-comparison'}
				components={[
					<span key="normal">
						{i18n?.t('infReportYoutube:engagement.engagement-rate.normal')}
					</span>,
				]}
			/>
		),
		low: (
			<Trans
				// t={i18n?.t}
				i18nKey={'infReportYoutube:engagement.engagement-rate.low-comparison'}
				components={[
					<span key="low">
						{i18n?.t('infReportYoutube:engagement.engagement-rate.low')}
					</span>,
				]}
			/>
		),
		unmeasurable: (
			<Trans
				// t={i18n?.t}
				i18nKey={'infReportYoutube:engagement.engagement-rate.unmeasurable-comparison'}
				components={[
					<span key="unmeasurable">
						{i18n?.t('infReportYoutube:engagement.engagement-rate.unmeasurable')}
					</span>,
				]}
			/>
		),
	} as const;

	/**
	 * @description 참여율(ER) 비교 결과 상태
	 */
	get status() {
		const hasAllValue = this.realEngagement && this.avgRealEngagement;
		if (!hasAllValue) return 'unmeasurable';

		return this.realEngagement > this.avgRealEngagement * 1.1
			? 'high'
			: this.realEngagement < this.avgRealEngagement * 0.9
			? 'low'
			: 'normal';
	}

	/**
	 * @description 인플루언서 참여율(ER) 차트데이터 (주 데이터)
	 */
	get infAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.realEngagement && this.avgRealEngagement;
		return {
			value: `${roundFloat(this?.realEngagement ?? 0, 'down', 2)}%`,
			label: this.value?.account?.profile?.username ?? '',
			valuePercentage: hasAllValue
				? (this.realEngagement / (this.realEngagement + this.avgRealEngagement)) * 100
				: undefined,
			colorType: this.status === 'high' ? 'primary' : 'black',
			isMain: true,
		};
	}

	/**
	 * @description 유사 그룹 평균 참여율(ER) 차트데이터 (비교군 데이터)
	 */
	get similarGroupAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.realEngagement && this.avgRealEngagement;
		return {
			value: `${roundFloat(this?.avgRealEngagement ?? 0, 'down', 2)}%`,
			label: i18n?.t('infReportYoutube:engagement.similar-group.text'), //유사 그룹 평균
			valuePercentage: hasAllValue
				? (this.avgRealEngagement / (this.realEngagement + this.avgRealEngagement)) * 100
				: undefined,
			colorType: 'gray',
		};
	}

	/**
	 * @description 차트 데이터 전체
	 */
	get data(): BoldVerticalBarChartData[] {
		const realEngagementDefaultData = {
			label: this.value?.account?.profile?.username ?? '',
		};
		const avgRealEngagementDefaultData = {
			label: i18n?.t('infReportYoutube:engagement.similar-group.text'),
		};

		const realEngagementData = formatBoldVerticalChartData(
			this.realEngagement,
			realEngagementDefaultData,
			this.infAdvancedData,
		);

		const avgRealEngagementData = formatBoldVerticalChartData(
			this.avgRealEngagement,
			avgRealEngagementDefaultData,
			this.similarGroupAdvancedData,
		);

		return [realEngagementData, avgRealEngagementData];
	}

	get valueText(): string {
		if (!this.realEngagement)
			return i18n?.t('infReportYoutube:engagement.real-engagement.preparing') ?? '';

		return `${roundFloat(this.realEngagement, 'down', 2)}%`;
	}

	adapt(): BoldVerticalBarChartProps {
		if (this.realEngagement === undefined) return { isError: true };

		return {
			desc: (
				<Trans
					// t={i18n?.t}
					i18nKey={'infReportYoutube:engagement.real-engagement.description'}
					components={[
						<span key="realEngagement">{this.valueText}</span>,
						this.textMapper[this.status],
					]}
				/>
			),
			data: this.data,
		};
	}
}

export class EngagementYtAdapter {
	value: YTReportRes;

	constructor(obj: YTReportRes) {
		this.value = obj;
	}

	adapt(): EngagementYtProps {
		const averageViews = new AverageViewsAdapter(this.value).adapt();
		const averageLikes = new AverageLikesAdapter(this.value).adapt();
		const averageComments = new AverageCommentsAdapter(this.value).adapt();
		const engagementRate = new EngagementRateAdapter(this.value).adapt();

		return {
			averageViews,
			averageLikes,
			averageComments,
			engagementRate,
		};
	}
}
